import { useEffect, useMemo, useState } from 'react';
import { CreditManagerArPerformanceRow, formatMoney } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { getCreditManagerArPerformance } from '../../../../../redux/services/reportsService';
import { DillTable, DillColumn, DillSelect } from '../../../../general/Dill';

const PERIOD_OPTIONS = [
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 60 days', value: '60' },
  { label: 'Last 90 days', value: '90' },
];

const CreditManagerPerformanceTable = () => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { creditManagerArPerformanceReport, loadingList } = useAppSelector(
    (state) => state.reports
  );
  const [days, setDays] = useState('30');

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(
        getCreditManagerArPerformance({ supplierId: currentSupplier.id, days: Number(days) })
      );
    }
  }, [currentSupplier?.id, days]);

  const isLoading = loadingList.includes('getCreditManagerArPerformance');
  const rows = creditManagerArPerformanceReport?.rows ?? [];

  const columns: DillColumn<CreditManagerArPerformanceRow>[] = useMemo(
    () => [
      {
        key: 'creditManagerName',
        label: 'Credit Manager',
        width: 200,
        sortable: true,
        getSortValue: (row) => row.creditManagerName,
        render: (_, row) => (
          <p className="text-xs font-medium text-gray-700">{row.creditManagerName}</p>
        ),
      },
      {
        key: 'accountsAssignedCount',
        label: 'Accounts Assigned',
        width: 150,
        numeric: true,
        sortable: true,
        render: (v: number) => <p className="text-xs w-full text-right">{v}</p>,
      },
      {
        key: 'accountsPastDueCount',
        label: '# Accounts Past Due',
        width: 150,
        numeric: true,
        sortable: true,
        defaultSortDir: 'desc',
        render: (v: number) => (
          <p className={`text-xs w-full text-right ${v > 0 ? 'text-red-500 font-medium' : ''}`}>
            {v}
          </p>
        ),
      },
      {
        key: 'amountPastDue',
        label: '$ Past Due',
        width: 130,
        numeric: true,
        sortable: true,
        defaultSortDir: 'desc',
        render: (v: number) => (
          <p className={`text-xs w-full text-right ${v > 0 ? 'text-red-500 font-medium' : ''}`}>
            {formatMoney(v, 0)}
          </p>
        ),
      },
      {
        key: 'averageDso',
        label: 'Average DSO',
        width: 130,
        numeric: true,
        sortable: true,
        getSortValue: (row) => row.averageDso ?? -1,
        render: (v: number | null) => (
          <p className="text-xs w-full text-right text-gray-700">{v != null ? `${v} days` : '—'}</p>
        ),
      },
      {
        key: 'reminderEmailsSentCount',
        label: 'Reminder Emails Sent',
        width: 160,
        numeric: true,
        sortable: true,
        render: (v: number) => <p className="text-xs w-full text-right">{v}</p>,
      },
      {
        key: 'collectionCallsCount',
        label: 'Collection Calls',
        width: 150,
        numeric: true,
        sortable: true,
        render: (v: number) => <p className="text-xs w-full text-right">{v}</p>,
      },
    ],
    []
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow duration-300">
      <div className="flex items-center justify-between mb-4 gap-4">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
            Performance by Credit Manager
          </h3>
          <p className="text-sm text-gray-500">
            Account health and collections activity for each assigned credit manager
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="min-w-[140px]">
            <DillSelect value={days} options={PERIOD_OPTIONS} onChange={setDays} />
          </div>
          <span className="text-xs font-medium px-3 py-1 rounded-full bg-blue-50 text-blue-600 whitespace-nowrap">
            {rows.length} {rows.length === 1 ? 'manager' : 'managers'}
          </span>
        </div>
      </div>
      <div>
        <DillTable<CreditManagerArPerformanceRow>
          columns={columns}
          data={rows}
          rowKey={(row) => row.creditManagerId}
          sortable
          search={false}
          loading={isLoading}
          striped={false}
          showCount={false}
          resizable
          emptyMessage="No customers are currently assigned to a credit manager."
        />
      </div>
    </div>
  );
};

export default CreditManagerPerformanceTable;
